import { Fieldset, Legend } from '@headlessui/react'
import { FragFinderHeading } from '../../components/FragFinderHeading'
import { SingleTextField } from '../../components/forms/SingleTextField'
import { MultiTextField } from '../../components/forms/MultiTextField'
import { BackButton } from '../../components/BackButton'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { MediaField } from '../../components/forms/MediaField'
import { IOption } from '../../types'
import { YearDatePickerField } from '../../components/forms/YearDatePickerField'
import { TagComboBoxField } from '../../components/forms/TagComboBoxField'
import { usePbMetadataStore } from '../../stores/pbMetadataStore'
import { useAuthStore } from '../../data/useAuthStore'
import { UnauthorizedAccess } from '../../components/UnauthorizedAccess'
import { BlackActionButton } from '../../components/buttons/BlackActionButton'
import { createPendingSubmission, getPendingSubmissionById, updatePendingSubmission } from '../../pocketbase/PocketbasePendingSubmissionsService'
import { pb } from '../../pocketbase/Pocketbase'
import { useFormValidation } from './useFormValidation'
import { OkDialog } from '../../components/dialogs/OkDialog'
import { sanitizeText, normalizeString } from '../../utils/stringUtils'
import { SearchComboBoxField, CUSTOM_OPTION_ID } from '../../components/forms/SearchComboBoxField'
import { getAllApprovedSubmissionNames, IApprovedSubmissionName } from '../../pocketbase/PocketbaseMetadataService'
import { AdminSubmissionSuggestions } from '../../components/forms/AdminSubmissionSuggestions'

export const SubmitPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fragranceId = searchParams.get('id')
  const isEditMode = !!fragranceId
  const isAdmin = useAuthStore((state) => state.isAdmin)
  const user = useAuthStore((state) => state.user)

  const { brands, notes, perfumers, countries, prices, isLoading: isLoadingMetadata, fetchAllMetadata } = usePbMetadataStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(isEditMode)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [navigationPath, setNavigationPath] = useState('')

  // Convert metadata to IOption format
  const brandOptions: IOption[] = brands.map(brand => ({ id: brand.id, label: brand.name }))
  const noteOptions: IOption[] = notes.map(note => ({
    id: note.id,
    label: note.name,
    imageUrl: note.image ? pb.files.getURL({ id: note.id, collectionName: 'notes' }, note.image, { thumb: '100x100' }) : null
  }))
  const perfumerOptions: IOption[] = perfumers.map(perfumer => ({ id: perfumer.id, label: perfumer.name }))
  const countryOptions: IOption[] = countries.map(country => ({ id: country.id, label: country.name }))
  const priceOptions: IOption[] = prices.map(price => ({ id: price.id, label: price.name }))

  // Form state
  const [selectedBrand, setSelectedBrand] = useState<IOption | null>(null)
  const [fragranceName, setFragranceName] = useState('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<IOption | null>(null)
  const [year, setYear] = useState<number | null>(null)
  const [selectedPrice, setSelectedPrice] = useState<IOption | null>(null)
  const [selectedPerfumer, setSelectedPerfumer] = useState<IOption | null>(null)
  const [topNotes, setTopNotes] = useState<IOption[]>([])
  const [middleNotes, setMiddleNotes] = useState<IOption[]>([])
  const [bottomNotes, setBottomNotes] = useState<IOption[]>([])
  const [approvedSubmissionNames, setApprovedSubmissionNames] = useState<IApprovedSubmissionName[]>([])
  const [isFragranceNameFocused, setIsFragranceNameFocused] = useState(false)

  // Validation hook
  const { validationErrors, validateForm, clearAllErrors } = useFormValidation({
    selectedBrand,
    fragranceName,
    selectedImage,
    existingImageUrl,
    description,
    selectedCountry,
    year,
    selectedPrice,
    selectedPerfumer,
    topNotes,
    middleNotes,
    bottomNotes
  })

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!isEditMode) {
      fetchAllMetadata(isAdmin)
    }
  }, [isAdmin])

  // Load fragrance when both admin auth and metadata are ready
  useEffect(() => {
    if (isEditMode && fragranceId && isAdmin) {
      loadFragrance(fragranceId)
    }
  }, [isEditMode, fragranceId, isAdmin])

  // Fetch approved submission names for admin suggestions
  useEffect(() => {
    const fetchApprovedNames = async () => {
      if (isAdmin) {
        try {
          const names = await getAllApprovedSubmissionNames()
          setApprovedSubmissionNames(names)
        } catch (error) {
          console.error('Failed to fetch approved submission names:', error)
        }
      }
    }
    fetchApprovedNames()
  }, [isAdmin])

  const loadFragrance = async (id: string) => {
    try {
      setIsLoadingData(true)

      await fetchAllMetadata(isAdmin)

      // Get fresh metadata to avoid stale closures
      const { brands, notes, perfumers, countries, prices } = usePbMetadataStore.getState()

      // Fetch the pending submission
      const submission = await getPendingSubmissionById(id)

      // Helper to parse JSON arrays
      const parseJsonArray = (value: string[] | string | null | undefined): string[] => {
        if (!value) return []
        if (Array.isArray(value)) return value
        try {
          const parsed = JSON.parse(value)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }

      // Populate form fields
      setFragranceName(submission.name || '')
      setDescription(submission.description || '')
      setYear(submission.year || null)

      // Brand - check if it's a relation or custom text
      if (submission.brand) {
        const brand = brands.find(b => b.id === submission.brand)
        if (brand) {
          setSelectedBrand({ id: brand.id, label: brand.name })
        }
      } else if (submission.brand_text) {
        // Custom brand text - create a temporary option
        setSelectedBrand({ id: CUSTOM_OPTION_ID, label: submission.brand_text })
      }

      // Country - check if it's a relation or custom text
      if (submission.country) {
        const country = countries.find(c => c.id === submission.country)
        if (country) {
          setSelectedCountry({ id: country.id, label: country.name })
        }
      } else if (submission.country_text) {
        setSelectedCountry({ id: CUSTOM_OPTION_ID, label: submission.country_text })
      }


      // Perfumer - check if it's a relation or custom text (take first one if multiple)
      if (submission.perfumers && Array.isArray(submission.perfumers) && submission.perfumers.length > 0) {
        const perfumerId = submission.perfumers[0]
        const perfumer = perfumers.find(p => p.id === perfumerId)
        if (perfumer) {
          setSelectedPerfumer({ id: perfumer.id, label: perfumer.name })
        }
      } else if (submission.perfumers_text) {
        const perfumerTexts = parseJsonArray(submission.perfumers_text)
        if (perfumerTexts.length > 0) {
          setSelectedPerfumer({ id: CUSTOM_OPTION_ID, label: perfumerTexts[0] })
        }
      }


      // Price
      if (submission.price) {
        const price = prices.find(p => p.id === submission.price)
        if (price) {
          setSelectedPrice({ id: price.id, label: price.name })
        }
      }

      // Notes - combine relations and custom text
      const loadNotes = (relationIds: string[] | string | undefined, customText: string[] | string | null | undefined): IOption[] => {
        const options: IOption[] = []

        // Add relation notes
        if (relationIds) {
          const ids = Array.isArray(relationIds) ? relationIds : [relationIds]
          ids.forEach(id => {
            const note = notes.find(n => n.id === id)
            if (note) {
              options.push({ id: note.id, label: note.name })
            }
          })
        }

        // Add custom text notes
        const customNotes = parseJsonArray(customText)
        customNotes.forEach(label => {
          options.push({ id: Date.now().toString() + Math.random(), label })
        })

        return options
      }

      setTopNotes(loadNotes(submission.top_notes, submission.top_notes_text))
      setMiddleNotes(loadNotes(submission.middle_notes, submission.middle_notes_text))
      setBottomNotes(loadNotes(submission.bottom_notes, submission.bottom_notes_text))

      // Load existing image URL for preview
      if (submission.image) {
        const imageUrl = pb.files.getURL(submission, submission.image)
        setExistingImageUrl(imageUrl)
      }

    } catch (error) {
      console.error('Error loading submission:', error)
      alert('Failed to load submission')
      navigate('/dashboard')
    } finally {
      setIsLoadingData(false)
    }
  }

  // Check admin access for edit mode
  if (isEditMode && !isAdmin) {
    return <UnauthorizedAccess />
  }

  // Show loading state while fetching fragrance data
  if (isLoadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl">Loading...</div>
      </div>
    )
  }

  const onImageSelected = (file: File) => {
    // Store the file for later upload on form submission
    setSelectedImage(file)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    const { isValid } = validateForm()
    if (!isValid) {
      return
    }

    if (!user) {
      alert('You must be logged in to submit a fragrance')
      return
    }

    // Clear any previous validation errors
    clearAllErrors()

    try {
      setIsSubmitting(true)

      // Helper to resolve entity (Brand, Country, Perfumer)
      const resolveEntity = <T extends { id: string, name: string }>(option: IOption | null, collection: T[], relationKey: string, textKey: string) => {
        if (!option) return {}

        // 1. Check if ID exists in collection (real relation)
        // Note: checking by ID first handles cases where the user selected an existing item
        if (option.id !== CUSTOM_OPTION_ID && collection.some(item => item.id === option.id)) {
          return { [relationKey]: option.id }
        }

        // 2. Check if normalized label matches any existing item name (case-insensitive, accent-insensitive)
        const normalizedLabel = normalizeString(option.label)
        const match = collection.find(item => normalizeString(item.name) === normalizedLabel)

        if (match) {
          return { [relationKey]: match.id }
        }

        // 3. It's a new custom value
        return { [textKey]: sanitizeText(option.label) }
      }

      // Separate notes into existing (from DB) and custom (user-created)
      const separateNotes = (notes: IOption[]) => {
        const existing: string[] = []
        const custom: string[] = []

        notes.forEach(note => {
          // Check if note exists in the database by ID
          const existsInDb = noteOptions.some(dbNote => dbNote.id === note.id && dbNote.id !== CUSTOM_OPTION_ID)

          if (existsInDb) {
            existing.push(note.id)
          } else {
            // Check if normalized label matches any existing note name (case-insensitive, accent-insensitive)
            const normalizedLabel = normalizeString(note.label)
            const match = noteOptions.find(n => normalizeString(n.label) === normalizedLabel)

            if (match) {
              existing.push(match.id)
            } else {
              // Sanitize custom note labels
              custom.push(sanitizeText(note.label))
            }
          }
        })

        return { existing, custom }
      }

      const topNotesData = separateNotes(topNotes)
      const middleNotesData = separateNotes(middleNotes)
      const bottomNotesData = separateNotes(bottomNotes)

      // Resolve Perfumer (special handling for array structure)
      const resolvedPerfumer = resolveEntity(selectedPerfumer, perfumers, 'perfumers', 'perfumers_text')
      const perfumerData = Object.keys(resolvedPerfumer).reduce((acc, key) => {
        acc[key] = [resolvedPerfumer[key]]
        return acc
      }, {} as any)

      // Create submission object with sanitized text fields
      const submission = {
        name: sanitizeText(fragranceName),
        description: description ? description.trim() : undefined,
        year: year || undefined,

        // Brand
        ...resolveEntity(selectedBrand, brands, 'brand', 'brand_text'),

        // Country
        ...resolveEntity(selectedCountry, countries, 'country', 'country_text'),

        // Perfumer
        ...perfumerData,

        // Price - always a relation (no custom prices allowed)
        price: selectedPrice!.id,

        // Notes - always include arrays (empty arrays will be converted to null in update)
        top_notes: topNotesData.existing,
        top_notes_text: topNotesData.custom,
        middle_notes: middleNotesData.existing,
        middle_notes_text: middleNotesData.custom,
        bottom_notes: bottomNotesData.existing,
        bottom_notes_text: bottomNotesData.custom,

        // Image file
        image: selectedImage || undefined,
      }

      if (isEditMode && fragranceId) {
        // Update existing submission
        await updatePendingSubmission(fragranceId, submission)
        setSuccessMessage('Submission updated successfully!')
        setNavigationPath('/dashboard/submissions')
        setShowSuccessDialog(true)
      } else {
        // Create new submission
        await createPendingSubmission(submission)
        setSuccessMessage('Fragrance submitted successfully! It will be reviewed by an admin before being published.')
        setNavigationPath('/dashboard/myfragrances')
        setShowSuccessDialog(true)
      }

    } catch (error) {
      console.error('Error submitting fragrance:', error)
      setShowErrorDialog(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className={'min-h-screen flex flex-col items-center bg-background'}>
        <div className={'w-full px-[10%] mt-15'}>
          <BackButton onClick={() => navigate(-1)} />
        </div>

        <FragFinderHeading
          className={'text-[54px] mb-4 mt-15'}
          text={isEditMode ? 'Edit Fragrance' : 'Submit a Fragrance'}
        />

        {!isEditMode && (
          <div
            className={
              'text-2xl text-center mb-12 md:w-1/2 w-4/5 font-sf-pro-light'
            }
          >
            Help us sniff out the perfect fragrance! Whether it's smoky and
            mysterious or fresh like a spring fling, share a scent below. Your
            addition might just help someone find their next signature fragrance.
          </div>
        )}

        <section className={'max-w-[980px] w-full mx-auto px-4 py-8'}>
          <form className={'space-y-6'} onSubmit={handleSubmit}>
            <Fieldset
              className={'md:space-y-8 space-y-6 bg-white md:p-10 p-6 mb-10'}
            >
              <Legend
                className={'text-3xl md:pb-8 pb-6 border-b-1 border-formSep'}
              >
                Details
              </Legend>
              <SearchComboBoxField
                label={'Brand'}
                placeholder={isLoadingMetadata ? 'Loading brands...' : 'Select a brand'}
                options={brandOptions}
                value={selectedBrand}
                onSelect={setSelectedBrand}
                error={validationErrors.has('brand')}
                maxLength={60}
              />
              <div className="relative">
                <SingleTextField
                  label={'Fragrance name'}
                  placeholder={'Add a name'}
                  value={fragranceName}
                  onChange={setFragranceName}
                  error={validationErrors.has('name')}
                  maxLength={120}
                  onFocus={() => setIsFragranceNameFocused(true)}
                  onBlur={() => setIsFragranceNameFocused(false)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                    }
                  }}
                />
                {isAdmin && (
                  <AdminSubmissionSuggestions
                    query={fragranceName}
                    suggestions={approvedSubmissionNames}
                    visible={isFragranceNameFocused}
                  />
                )}
              </div>
              <MediaField
                label={'Media'}
                onImageSelected={onImageSelected}
                initialImageUrl={existingImageUrl}
                error={validationErrors.has('media')}
              />
              <MultiTextField
                label={'Description'}
                placeholder={'Write a description'}
                value={description}
                onChange={setDescription}
                error={validationErrors.has('description')}
                maxLength={2000}
              />
              <SearchComboBoxField
                label={"Where it's from"}
                placeholder={isLoadingMetadata ? 'Loading countries...' : 'Add a country'}
                options={countryOptions}
                value={selectedCountry}
                onSelect={setSelectedCountry}
                error={validationErrors.has('country')}
                maxLength={40}
              />
              <YearDatePickerField
                label={"Year it's made"}
                placeholder={'Add a year'}
                value={year}
                onChange={setYear}
                error={validationErrors.has('year')}
              />
              <SearchComboBoxField
                label={'Price'}
                placeholder={isLoadingMetadata ? 'Loading prices...' : 'Add a price'}
                options={priceOptions}
                readOnly={true}
                value={selectedPrice}
                onSelect={setSelectedPrice}
                error={validationErrors.has('price')}
              />

              <SearchComboBoxField
                label={"Created by"}
                placeholder={isLoadingMetadata ? 'Loading perfumers...' : 'Add a name'}
                options={perfumerOptions}
                value={selectedPerfumer}
                onSelect={setSelectedPerfumer}
                error={validationErrors.has('perfumer')}
                maxLength={80}
              />
            </Fieldset>
            <Fieldset
              className={'md:space-y-8 space-y-6 bg-white md:p-10 p-6 mb-10'}
            >
              <Legend
                className={'text-3xl md:pb-8 pb-6 border-b-1 border-formSep'}
              >
                Ingredients
              </Legend>
              <TagComboBoxField
                label={'Top notes'}
                placeholder={isLoadingMetadata ? 'Loading notes...' : 'Add note'}
                options={noteOptions}
                value={topNotes}
                onChange={setTopNotes}
                error={validationErrors.has('topNotes')}
                maxLength={40}
              />
              <TagComboBoxField
                label={'Middle notes'}
                placeholder={isLoadingMetadata ? 'Loading notes...' : 'Add note'}
                options={noteOptions}
                value={middleNotes}
                onChange={setMiddleNotes}
                error={validationErrors.has('middleNotes')}
                maxLength={40}
              />
              <TagComboBoxField
                label={'Base notes'}
                placeholder={isLoadingMetadata ? 'Loading notes...' : 'Add note'}
                options={noteOptions}
                value={bottomNotes}
                onChange={setBottomNotes}
                error={validationErrors.has('bottomNotes')}
                maxLength={40}
              />
            </Fieldset>
            <div className={'text-center'}>
              <BlackActionButton
                type="submit"
                label={isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update fragrance' : 'Submit fragrance')}
              />
            </div>
          </form>
        </section>
      </div>

      <OkDialog
        isOpen={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false)
          navigate(navigationPath)
        }}
        title="Success"
        content={successMessage}
      />

      <OkDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        content="Failed to submit fragrance. Please try again."
      />
    </>
  )
}
